import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidGen } from 'uuid';
import { postVenueSearch, getCuisines } from '../api';
import { autocompleteSearchTransform, venueSearchTransform } from '../api/resyApiTransformer';
import {
  getAutocompleteCacheKey,
  getAutocompleteSearchPayload,
  getVenueSearchPayload,
} from '../utils/venueSearchSliceUtils';
import { selectExperiment } from '../components/Experiments/useExperiment';
import { CLASSIFY_INTENT_SIGNALS } from '../constants/optimizelyExperiments';

const loadAutocompleteSearchThunk = createAsyncThunk(
  'venueSearch/loadAutocompleteSearch',
  async (params, { rejectWithValue, getState }) => {
    const classifyIntentSendSessionId = selectExperiment(getState(), CLASSIFY_INTENT_SIGNALS);
    const payload = getAutocompleteSearchPayload(params, { includeTockInventory: true });
    const sessionId = classifyIntentSendSessionId.enabled ? getState().venueSearch?.metadata.searchSessionId : null;
    try {
      const response = await postVenueSearch(payload, sessionId);
      const parsedResponse = autocompleteSearchTransform(response);
      const cacheKey = getAutocompleteCacheKey(params);
      return { data: parsedResponse, cacheKey };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const loadVenueSearchThunk = createAsyncThunk(
  'venueSearch/loadVenueSearch',
  async (params, { rejectWithValue, getState }) => {
    const classifyIntentSendSessionId = selectExperiment(getState(), CLASSIFY_INTENT_SIGNALS);
    const payload = getVenueSearchPayload(params, { includeTockInventory: true });
    const sessionId = classifyIntentSendSessionId.enabled ? getState().venueSearch?.metadata.searchSessionId : null;
    try {
      const response = await postVenueSearch(payload, sessionId);
      return { ...venueSearchTransform(response), query: params.query, metadata: { searchSessionId: getState().venueSearch?.metadata.searchSessionId } };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const loadCuisinesThunk = createAsyncThunk(
  'venueSearch/loadCuisines', async (params, { rejectWithValue }) => {
    try {
      const response = await getCuisines(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

/*
 Janky, I know. But this needs to remain constant even within the widget, and we don't know where the state is being loaded from initially.
 This value will always stay synced to the window.
 */
const initialSearchSessionId = window.__SEARCH_SESSION_ID__ || new URLSearchParams(window.location.search).get('searchSessionId') || uuidGen(); // eslint-disable-line no-underscore-dangle
window.__SEARCH_SESSION_ID__ = initialSearchSessionId; // eslint-disable-line no-underscore-dangle

const venueSearchSlice = createSlice({
  name: 'venueSearch',
  initialState: {
    autocomplete: {
      cache: {},
      facetValues: [],
      hits: [],
      status: 'idle',
      error: null,
    },
    search: {
      cuisines: [],
      hits: [],
      nbPages: 0,
      status: 'idle',
      error: null,
    },
    metadata: {
      searchSessionId: initialSearchSessionId,
      previousQuery: {},
      lastUsedSearchEngine: '',
    },
  },
  reducers: {
    updateAutocompleteSearchFromCache: ({ autocomplete }, action) => {
      const cacheKey = action.payload;
      const data = autocomplete.cache[cacheKey];
      autocomplete.facetValues = data.facetValues;
      autocomplete.hits = data.hits;
    },
    generateNewSearchSession: ({ metadata }) => {
      metadata.searchSessionId = uuidGen();
      metadata.previousQuery = {};
      window.__SEARCH_SESSION_ID__ = metadata.searchSessionId; // eslint-disable-line no-underscore-dangle
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadAutocompleteSearchThunk.pending, ({ autocomplete }) => {
      autocomplete.status = 'pending';
      autocomplete.error = null;
    });
    builder.addCase(loadAutocompleteSearchThunk.fulfilled, ({ autocomplete, metadata }, action) => {
      autocomplete.status = 'success';
      const { payload: { data, cacheKey } } = action;
      autocomplete.facetValues = data.facetValues;
      autocomplete.hits = data.hits;
      autocomplete.cache[cacheKey] = data;
      metadata.lastUsedSearchEngine = data.meta?.engine ?? '';
    });
    builder.addCase(loadAutocompleteSearchThunk.rejected, ({ autocomplete }, action) => {
      autocomplete.status = 'error';
      const { data, status } = action.payload;
      autocomplete.error = action.error;
      autocomplete.error.data = data;
      autocomplete.error.status = status;
    });
    builder.addCase(loadVenueSearchThunk.pending, ({ search, metadata }, action) => {
      search.status = 'pending';
      search.error = null;

      const { meta: { arg: { query } } } = action;

      if (!metadata.previousQuery.type || (query.type !== metadata.previousQuery.type && query.value === '')
        || query.value !== metadata.previousQuery.value) {
        metadata.searchSessionId = uuidGen();
        window.__SEARCH_SESSION_ID__ = metadata.searchSessionId; // eslint-disable-line no-underscore-dangle
      }
      metadata.previousQuery = query;
    });
    builder.addCase(loadVenueSearchThunk.fulfilled, ({ search }, action) => {
      search.status = 'success';
      const { payload: { cuisines, results: [{ hits, nbPages }] } } = action;
      search.cuisines = cuisines ?? [];
      search.hits = hits;
      search.nbPages = nbPages;
    });
    builder.addCase(loadVenueSearchThunk.rejected, ({ search }, action) => {
      search.status = 'error';
      const { data, status } = action.payload;
      search.error = action.error;
      search.error.data = data;
      search.error.status = status;
    });
  },
});
const { updateAutocompleteSearchFromCache, generateNewSearchSession } = venueSearchSlice.actions;
const getAutocompleteSearchThunk = (params) => (dispatch, getState) => {
  const { venueSearch: slice } = getState();
  const cacheKey = getAutocompleteCacheKey(params);
  if (slice.autocomplete.cache[cacheKey]) {
    return Promise.resolve(dispatch(updateAutocompleteSearchFromCache(cacheKey)));
  }
  if (slice.autocomplete.status !== 'pending') {
    return dispatch(loadAutocompleteSearchThunk(params));
  }
  return Promise.resolve({});
};

const generateNewSearchSessionThunk = () => (dispatch) => dispatch(generateNewSearchSession());

const selectSearchSessionId = (state) => state?.venueSearch?.metadata?.searchSessionId ?? '';

export {
  generateNewSearchSessionThunk,
  getAutocompleteSearchThunk,
  loadAutocompleteSearchThunk,
  loadCuisinesThunk,
  loadVenueSearchThunk as getVenueSearchThunk,
  selectSearchSessionId,
};
export default venueSearchSlice.reducer;
